<script lang="ts" setup>
import {
  isLockMsg,
  replacePlaceHolder,
} from "@/components/mobile/chat/mode/util";
import { BubbleActionPopupInstance } from "@/components/mobile/chat/popup/BubbleActionPopup.vue";
import { WaitingSessionCreationInstance } from "@/components/mobile/chat/WaitingSessionCreation.vue";
import { useChatMessageList } from "@/composable/chat/useChatMessageList";
import { useMateChatHead } from "@/composable/head/useMateChatHead";
import { DEFAULT_COST, ModelList } from "@/enums/chatEnums";
import api from "@/modules/api";
import { isMobile } from "@/modules/common";
import { useAudio } from "@/stores/audio";
import { useChat } from "@/stores/chat";
import { useChatSession } from "@/stores/chatSession";
import { useMate } from "@/stores/mate";
import { ChatMessageItemInstance } from "@/types/component";
import { ChatMessage, Mate, MateSession } from "@/types/entity";
import { useRawImageUrl } from "@/use/common";
import { goMateProfile } from "@/use/go";
import { useTriggerLoginForChat } from "@/use/useTriggerLogin";
import { useElementBounding } from "@vueuse/core";
import { useRouteParams } from "@vueuse/router";
import { marked } from "marked";
import { List, ListInstance, Popup, showImagePreview } from "vant";
import { computed, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";

const routeName = "mateChat";

// 状态
const chat = useChat();
const mateStore = useMate();
const route = useRoute();
const targetMateUuid = useRouteParams("uuid", "");
const mateSessionId = useRouteParams("mateSessionId", 0, {
  transform: Number,
});
const sessionStore = useChatSession();
const audioStore = useAudio();

const scrollRef = ref<HTMLDivElement | null>(null);
const isInputFocus = ref(false);
const { height: msgContainerHeight } = useElementBounding(scrollRef);
const maxHeightOfMsg = computed(() => Math.floor(msgContainerHeight.value / 2));
const messageContainerRef = ref<HTMLElement | null>(null);
const currentSession = computed<MateSession | null>(() => {
  return sessionStore.sessionMap[mateSessionId.value];
});
const isPrivate = computed(() => {
  if (!currentSession.value) {
    return false;
  }
  return currentSession.value?.isPrivate === 1;
});
const chatMessageRefs = ref<ChatMessageItemInstance[]>([]);
const myMessage = ref<string>("");
const isStreamReplying = ref(false);
const streamMsg = ref<string>("");
const isPostingMsg = ref<"start" | "end" | false>(false);
const isHistoryMode = ref(!isPrivate.value);
const showBioPopup = ref(false);
const listRef = ref<ListInstance | null>(null);
const waitingRef = ref<WaitingSessionCreationInstance | null>(null);
const bubbleActionRef = ref<BubbleActionPopupInstance | null>(null);
const showActions = computed(() => {
  if (!isPrivate.value) return false;
  return !isInputFocus.value;
});
const hideMsgUI = ref(false);
const targetMate = ref<Mate | null>(null);
useMateChatHead(targetMate);
const defaultBgUrl = computed(
  () =>
    currentSession.value?.chatScene?.media?.url ??
    targetMate.value?.sceneMedia?.url ??
    targetMate.value?.mediaUrl
);
const showChatSetting = ref(false);
const isLoading = ref(false);
const { shouldPopLogin } = useTriggerLoginForChat(currentSession);
const mateImageList = ref<MateMedia[]>([]);
const scenario = computed(() => {
  const scenarioText =
    currentSession.value?.chatScene?.scenario || targetMate.value?.scenario;
  if (
    !scenarioText ||
    !targetMate.value?.name ||
    !mateStore.currentMate?.name
  ) {
    return "";
  }
  return marked.parse(
    replacePlaceHolder(
      scenarioText,
      targetMate.value.name,
      mateStore.currentMate?.name
    )
  );
});
// Lifecycle
const prepareData = async () => {
  isLoading.value = true;
  try {
    const session = await sessionStore.getOrFetchMateSessionItem(
      mateSessionId.value
    );
    const hasImageMsg = targetMate.value?.mateEmotionCount;
    const targetMatePromise = mateStore.getOrFetchMate(
      targetMateUuid.value,
      !hasImageMsg
    );
    // 1. 获取聊天记录
    const messageListPromise = chat.initMessageList(mateSessionId.value);
    // 2. 获取聊天对象信息
    const mateMediaPromise = api.readMateMediaPage({
      filter: {
        mateId: session.mateId!,
      },
      pagination: { page: 1, perPage: 9 },
      sort: { field: "id", order: "desc" },
    });
    // 3. 自拍数刷新
    const chatImageCountPromise = mateStore.refreshChatImageCount(
      targetMateUuid.value
    );
    const [{ page }, fullMate] = await Promise.all([
      mateMediaPromise,
      targetMatePromise,
      messageListPromise,
      chatImageCountPromise,
    ]);
    targetMate.value = fullMate;
    mateImageList.value = page.data;
  } finally {
    isLoading.value = false;
  }
};

watch(
  [mateSessionId, targetMateUuid],
  async ([sessionId, mateUuid], [oldSessionId, oldMateUuid]) => {
    if (
      route.name !== routeName ||
      isLoading.value ||
      !sessionId ||
      !mateUuid ||
      (sessionId === oldSessionId && mateUuid === oldMateUuid)
    )
      return;
    isLoading.value = true;
    await prepareData();
    bgUrl.value = defaultBgUrl.value;
    await nextTick();
    debounceScrollToBottom();
    if (audioStore.enableAutoPlay && messageList.value.length > 0) {
      playAudioMsg(messageList.value[messageList.value.length - 1].id);
    }
  },
  { immediate: true }
);

watch(
  isHistoryMode,
  (isHistoryMode) => {
    if (isHistoryMode) {
      scrollToBottom(false);
      listRef.value?.check();
    }
  },
  { flush: "post" }
);

const {
  messageList,
  scrollLoading,
  finished,
  error,
  scrollLoad,
  scrollToBottom,
  debounceScrollToBottom,
} = useChatMessageList({
  sessionId: mateSessionId.value,
  messageContainerRef,
  scrollRef,
  myMessage,
  streamMsg,
  isStreamReplying,
});
// 滑动加载

const playAudioMsg = (id: number) => {
  const target = chatMessageRefs.value.find((item) => item.id === id);
  if (target) {
    target.audioRef?.playAudio();
  }
};

const onStreamFinish = async (data: {
  userMsg?: ChatMessage;
  assistantMsg?: ChatMessage;
}) => {
  bgUrl.value = data.userMsg?.emotionMediaUrl
    ? data.userMsg.emotionMediaUrl
    : defaultBgUrl.value;
  await nextTick();
  if (audioStore.enableAutoPlay && data.assistantMsg) {
    playAudioMsg(data.assistantMsg.id);
  }
  if (data.userMsg?.type === "Continue") return;
};

const bgUrl = ref(defaultBgUrl.value);
const maskLinear =
  "linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.2), rgba(0,0,0,0.5)),";
const shallowLinear =
  "linear-gradient(rgba(0, 0, 0, 0.15) 0%,  rgba(0, 0, 0, 0) 70%),";
const backgroundStyle = computed(
  () => `${isHistoryMode.value ? maskLinear : shallowLinear} no-repeat`
);
const backgroundImage = computed(() => `url(${bgUrl.value})`);

const onMediaClick = (idx: number) => {
  const urls = mateImageList.value!.map((item) => item.media.url);
  showImagePreview(urls, idx);
};

const unlockCost = computed(() =>
  Number(
    ModelList.find(
      (item) =>
        item.value === currentSession.value?.model &&
        item.costType === "crystal"
    )?.cost ?? DEFAULT_COST
  )
);
</script>
<template>
  <div
    v-if="!isLoading"
    :style="{
      background: isMobile() ? 'none' : backgroundStyle,
      backgroundImage: isMobile() ? 'none' : useRawImageUrl(backgroundImage),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }"
    class="h-full overflow-hidden text-base relative transition-all duration-300"
  >
    <div class="absolute inset-0 z-0 transition-all duration-300">
      <div
        v-if="isMobile()"
        :style="{
          background: backgroundStyle,
          backgroundImage: useRawImageUrl(backgroundImage),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }"
        class="absolute inset-0 z-10 transition-all duration-300"
      />
      <div
        :style="{
          background: backgroundStyle,
        }"
        class="absolute inset-0 z-20 transition-all duration-300"
      />
    </div>
    <div class="relative h-full grid grid-rows-[auto_1fr_auto] z-30">
      <chat-header
        :current-session="currentSession"
        :is-loading="isLoading"
        :is-private="isPrivate"
        :is-scene-mode="false"
        :mate="targetMate"
      />
      <div ref="scrollRef" class="h-full overflow-y-auto">
        <div
          :class="{ hidden: hideMsgUI }"
          class="flex flex-col justify-end min-h-full p-2 w-full text-mode-v2"
        >
          <list
            ref="listRef"
            v-model:error="error"
            v-model:loading="scrollLoading"
            :disabled="!isHistoryMode"
            :error-text="$t('Message.requestFailedClickToReload')"
            :finished="finished"
            :finished-text="isHistoryMode ? $t('Message.noMoreAvailable') : ''"
            :scroller="isHistoryMode ? scrollRef : null"
            direction="up"
            offset="0"
            @load="scrollLoad"
          >
            <div
              v-if="
                scenario &&
                isPrivate &&
                ((messageList.length <= 1 && !streamMsg && !myMessage) ||
                  isHistoryMode)
              "
              class="chat-start chat"
            >
              <div
                class="p-4 rounded-xl relative overflow-hidden backdrop-blur-lg bg-black/70 text-white text-sm"
              >
                <div>
                  <span class="font-semibold text-base-content/50"
                    >{{ $t("User.mate.module.chatScene") }}:</span
                  >
                  <span class="chat-container dark" v-html="scenario" />
                </div>
              </div>
            </div>
            <div v-if="false" class="chat-start chat">
              <div
                v-if="
                  mateImageList.length > 0 &&
                  ((messageList.length <= 1 && !streamMsg && !myMessage) ||
                    isHistoryMode)
                "
                class="p-4 rounded-xl relative overflow-hidden backdrop-blur-lg bg-black/70 text-white lg:max-w-[400px] flex flex-col gap-2"
              >
                <div class="flex justify-between items-center">
                  <div class="font-semibold text-white/80">
                    {{ $t("Chats.recentPost") }}
                  </div>
                  <div
                    v-if="targetMate"
                    class="flex gap-1 text-sm text-white/80 cursor-pointer"
                    @click="goMateProfile(targetMate.uuid)"
                  >
                    <div>View All</div>
                    <i class="iconify mingcute--right-small-line" />
                  </div>
                </div>
                <div class="grid grid-cols-3 gap-0.5 rounded overflow-hidden">
                  <VanImage
                    v-for="(item, idx) in mateImageList"
                    :key="item.id"
                    :src="item.media.url"
                    class="aspect-square cursor-pointer"
                    fit="cover"
                    @click="onMediaClick(idx)"
                  />
                </div>
              </div>
            </div>
            <div ref="messageContainerRef">
              <ChatMessageItemV2
                v-for="(item, idx) in messageList"
                :id="item.id"
                :key="item.id"
                ref="chatMessageRefs"
                :audio-url="item.audioUrl"
                :avatar-url="item.mediaUrl"
                :class="{
                  hidden: !(
                    (!streamMsg &&
                      !myMessage &&
                      idx === messageList.length - 1) ||
                    isHistoryMode
                  ),
                }"
                :current-mate-tone-id="currentSession?.mateToneId"
                :enable-audio="!!item.msg && !item.isCurrentUser"
                :enable-reactive="showActions"
                :is-locked="isLockMsg(item)"
                :mate-uuid="item.mateUuid"
                :mateToneId="item.mateToneId || (item.isCurrentUser ? mateStore.currentMate!.mateToneId : (currentSession?.mateToneId || targetMate?.mateToneId))"
                :max-height="
                  !streamMsg &&
                  !myMessage &&
                  idx === messageList.length - 1 &&
                  !isHistoryMode
                    ? maxHeightOfMsg
                    : undefined
                "
                :media="item.media"
                :msg="item.msg"
                :position="item.isCurrentUser ? 'end' : 'start'"
                :require-unlock="item.requireUnlock"
                :time="!isPrivate ? item.time : undefined"
                :unlockCost="unlockCost"
                enable-avatar-click
                @on-long-press="
                  bubbleActionRef?.showBubbleActions(
                    item,
                    mateSessionId,
                    isPrivate,
                    idx === messageList.length - 1 && !item.isCurrentUser
                  )
                "
                @on-edit="bubbleActionRef?.editMsg(item, mateSessionId)"
              />
              <div>
                <ChatMessageItemV2
                  v-show="myMessage && (isHistoryMode || !streamMsg)"
                  :avatar-url="mateStore.currentMate!.mediaUrl"
                  :mate-uuid="mateStore.currentMate!.uuid"
                  :msg="myMessage"
                  :position="'end'"
                  enable-avatar-click
                  :enable-audio="false"
                />
                <ChatMessageItemV2
                  v-show="streamMsg"
                  :avatar-url="isPostingMsg === 'start' ? targetMate!.mediaUrl : mateStore.currentMate?.mediaUrl"
                  :enable-avatar-url="false"
                  :mate-uuid="targetMate!.uuid"
                  :msg="streamMsg"
                  :position="isPostingMsg === 'start' ? 'start' : 'end'"
                  enable-avatar-click
                  enable-parse-md
                  :enable-audio="false"
                />
                <ChatMessageItemV2
                  v-show="isPostingMsg"
                  :avatar-url="targetMate?.mediaUrl"
                  :mate-uuid="targetMate?.uuid"
                  :position="'start'"
                  enable-avatar-click
                  :enable-audio="false"
                >
                  <div class="flex items-center">
                    <span
                      class="loading loading-dots loading-sm text-zinc-800"
                    />
                  </div>
                </ChatMessageItemV2>
              </div>
            </div>
          </list>
        </div>
      </div>
      <ChatTextFooter
        v-if="targetMate"
        v-model:hideMsgUI="hideMsgUI"
        v-model:is-history-mode="isHistoryMode"
        v-model:is-input-focus="isInputFocus"
        v-model:is-posting-msg="isPostingMsg"
        v-model:is-stream-replying="isStreamReplying"
        v-model:my-message="myMessage"
        v-model:stream-msg="streamMsg"
        :currentSession="currentSession"
        :is-private="isPrivate"
        :mate="targetMate"
        :should-login="shouldPopLogin"
        enable-history-mode
        @onInputFocus="scrollToBottom(false)"
        @on-stream-finish="onStreamFinish"
      />
    </div>
    <Popup
      v-model:show="showBioPopup"
      class="max-w-[80%] md:max-w-md rounded-lg"
      overlay-class="backdrop-blur-lg"
      position="center"
    >
      <div class="p-6">{{ targetMate?.bio }}</div>
    </Popup>
    <BubbleActionPopup ref="bubbleActionRef" />
    <waiting-session-creation ref="waitingRef" />
    <chat-setting
      v-if="targetMate && isPrivate"
      v-model:show="showChatSetting"
      :session="currentSession"
      :session-id="mateSessionId"
      :target-mate="targetMate"
    />
  </div>
  <div v-else class="h-full w-full flex justify-center items-center">
    <span class="loading loading-sm" />
  </div>
</template>
